/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

 /*=============== Components ===============*/ 

 $(document).ready(function (){
    icons = {
        left: '<svg fill="#000000" height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg"><title>' + theme.strings.icon_labels_left + '</title><path d="M0 0h24v24H0z" fill="none"/><path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/></svg>',
        right: '<svg fill="#000000" height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg"><title>' + theme.strings.icon_labels_right + '</title><path d="M0 0h24v24H0z" fill="none"/><path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/></svg>',
        close: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" class="feather feather-x"><title>' + theme.strings.icon_labels_close + '</title><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
        chevronLeft: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" class="feather feather-chevron-left"><title>' + theme.strings.icon_labels_left + '</title><polyline points="15 18 9 12 15 6"></polyline></svg>',
        chevronRight: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" class="feather feather-chevron-right"><title>' + theme.strings.icon_labels_right + '</title><polyline points="9 18 15 12 9 6"></polyline></svg>',
        chevronDown: '<svg fill="#000000" height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg"><title>' + theme.strings.icon_labels_down + '</title><path d="M7.41 7.84L12 12.42l4.59-4.58L18 9.25l-6 6-6-6z"/><path d="M0-.75h24v24H0z" fill="none"/></svg>',
        tick: '<svg fill="#000000" height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg"><path d="M0 0h24v24H0z" fill="none"/><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>',
        label: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="feather feather-tag"><title>Label</title><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path><line x1="7" y1="7" x2="7" y2="7"></line></svg>' };
    $formContainer = $(document).find('.product-form');
    $formContainer.find('form.product-purchase-form').each(function () {
        var $form = $(this);
        $form.on('submit', function (evt) {
          // Validate
        //   if (!theme.validateProductForm($formContainer)) {
        //     return false;
        //   }
  
          // Ajax-add
          if (theme.settings.cartType === 'drawer' && $formContainer.is('[data-ajax-add-to-cart="true"]')) {
            evt.preventDefault();
            var cartPopupTemplate = [
            '<div id="cart-summary-overlay" class="cart-summary-overlay">',
            '<div class="cart-summary-overlay__row-head">',
            '<div class="cart-summary-overlay__column-image">',
            '<div class="cart-summary-overlay__column-title">', theme.strings.products_added_notification_title, '</div>',
            '</div>',
            '<div class="cart-summary-overlay__column-item">',
            '<div class="cart-summary-overlay__column-title">', theme.strings.products_added_notification_item, '</div>',
            '</div>',
            '<div class="cart-summary-overlay__column-price-region">',
            '<div class="cart-summary-overlay__column-price">',
            '<div class="cart-summary-overlay__column-title">', theme.strings.products_added_notification_unit_price, '</div>',
            '</div>',
            '<div class="cart-summary-overlay__column-quantity">',
            '<div class="cart-summary-overlay__column-title">', theme.strings.products_added_notification_quantity, '</div>',
            '</div>',
            '<div class="cart-summary-overlay__column-total">',
            '<div class="cart-summary-overlay__column-title">', theme.strings.products_added_notification_total_price, '</div>',
            '</div>',
            '</div>',
            '<div class="cart-summary-overlay__column-subtotal">',
            '<div class="cart-summary-overlay__column-title">', theme.strings.products_added_notification_subtotal, '</div>',
            '</div>',
            '</div>',
            '<div class="cart-summary-overlay__row-body">',
            '<div class="cart-summary-overlay__column-image">',
            '<img src="[[image_url]]" alt="[[encoded-title]]" />',
            '</div>',
            '<div class="cart-summary-overlay__column-item">',
            '<div class="cart-summary-overlay__title">[[title]]</div>',
            '<div class="cart-summary-overlay__bold_options">[[bold_options]]</div>',  
            '<div class="cart-summary-overlay__variants">[[variants]]</div>',
            '<div class="cart-summary-overlay__mobile-price-row">',
            '<div class="cart-summary-overlay__mobile-unit-price">[[unit_price]]</div>',
            '<div class="cart-summary-overlay__mobile-quantity">', theme.strings.products_added_notification_quantity,
            '<span class="cart-summary-overlay__quantity">[[quantity]]</span>',
            '</div>',
            '<div class="cart-summary-overlay__mobile-line-price"><span class="Bold-theme-hook-DO-NOT-DELETE bold_cart_item_total" style="display:none !important;"></span>[[line_price]]</div>',
            '</div>',
            '</div>',
            '<div class="cart-summary-overlay__column-price-region">',
            '<div class="cart-summary-overlay__column-price">',
            '<span class="cart-summary-overlay__unit-price">[[unit_price]]</span>',
            '</div>',
            '<div class="cart-summary-overlay__column-quantity">',
            '<span class="cart-summary-overlay__quantity">[[quantity]]</span>',
            '</div>',
            '<div class="cart-summary-overlay__column-total">',
            '<div class="cart-summary-overlay__line-price"><span class="Bold-theme-hook-DO-NOT-DELETE bold_cart_item_total_1"  style="display:none !important;"></span>[[line_price]]</div>',
            '</div>',
            '[[line_discount]]',
            '</div>',
            '<div class="cart-summary-overlay__column-subtotal">',
            '<span class="cart-summary-overlay__subtotal">[[subtotal]]</span>',
            '<div class="cart-summary-overlay__note">', theme.strings.products_added_notification_shipping_note, '&nbsp;</div>',
            '</div>',
            '</div>',
            '<div class="cart-summary-overlay__actions">',
            '<a id="shop-more" class="button altcolour" href="#">', theme.strings.products_added_notification_keep_shopping, '</a>',
            '<a class="button to-cart" href="', theme.routes.cart, '">', theme.strings.products_added_notification_cart, '</a>',
            '</div>',
            '</div>'].
            join('');
  
            var shopifyAjaxAddURL = theme.routes.cartAdd;
            var shopifyAjaxCartURL = theme.routes.cart + '.js';
  
            //Disable add button
            $form.find('button[type="submit"]').attr('disabled', 'disabled').each(function () {
              $(this).data('previous-value', $(this).val());
            }).val(theme.strings.products_product_adding_to_cart);
  
            //Hide any existing notifications
            $('#cart-summary-overlay #shop-more').triggerHandler('click');
  
            //Add to cart
            $.post(shopifyAjaxAddURL, $form.serialize(), function (itemData) {
              //Dispatch change event
              document.documentElement.dispatchEvent(
              new CustomEvent('theme:cartchanged', { bubbles: true, cancelable: false }));
  
  
              //Enable add button
              $form.find('button[type="submit"]').removeAttr('disabled').each(function () {
                $btn = $(this);
                //Set to 'DONE', alter button style, wait a few secs, revert to normal
                $btn.val(theme.strings.products_product_added_to_cart).addClass('inverted');
                window.setTimeout(function () {
                  $btn.removeClass('inverted').val($btn.data('previous-value'));
                }, 3000);
              });
  
              //Get our data
              var addedDataJSON = $.parseJSON(itemData);
     // Bold:POv2
              if(typeof window.BOLD !== 'undefined'
                 && typeof window.BOLD.common !== 'undefined'
                 && typeof window.BOLD.common.cartDoctor !== 'undefined') {
                addedDataJSON = window.BOLD.common.cartDoctor.fixItem(addedDataJSON);
              }              
              // Bold:POv2
              //Get current cart state
              $.get(shopifyAjaxCartURL, function (cartData) {
   
                var cartDataJSON = $.parseJSON(cartData),
                addedQty = addedDataJSON.quantity,
                addedImage = addedDataJSON.image,
                productData = getProductData($form),
                originalVariantPrice = addedDataJSON.original_price;
          
                // The only way to get the compare at price is from the in-page JSON dump
                for (var i = 0; i < productData.variants.length; i++) {
                  var variantData = productData.variants[i];
                  if (variantData.id == addedDataJSON.variant_id && variantData.compare_at_price && variantData.compare_at_price > originalVariantPrice) {
                    originalVariantPrice = variantData.compare_at_price;
                  }
                }
  
                // Variants require option names, which are not included in the cart
                var variantHtml = '';
                if (addedDataJSON.variant_title) {// catches default variant
                  for (var i = 0; i < productData.options.length; i++) {
                    variantHtml += [
                    '<div class="cart-summary-overlay__variant">',
                    '<span class="cart-summary-overlay__variant-option">', productData.options[i].name, '</span>',
                    '<span class="cart-summary-overlay__variant-value">', addedDataJSON.variant_options[i], '</span>',
                    '</div>'].join('');
                  }
                }
                // Bold:POv2
              if (addedDataJSON.properties) {
                var boldProperties=" ";
                var cart_items_html=" ";
                $.each(addedDataJSON.properties, function(jndex, jtem) {
                  if (jndex) {
                    boldProperties+= '<span class="bold_option_title">' + jndex + ' </span>';
                  }
                  var boldStripper = jtem.split(',');
                  $.each(boldStripper, function(l, ktem) {
                    if (ktem.toLowerCase().indexOf("/uploads/") >= 0) {
                      boldProperties+= '<span class="bold_option_value_title"><a href="' + ktem + '">FILE LINK</a></span>';
                    } else {
                      boldProperties+= '<span class="bold_option_value_title">'+ktem + '</span>';
                    }
                  });
                  boldProperties+= '<br/>';
                });
                cart_items_html += '<div class="line-item">' + boldProperties + '</div>';
              }
              // Bold:POv2
                if (addedDataJSON.selling_plan_allocation && addedDataJSON.selling_plan_allocation.selling_plan.name) {
                  variantHtml += [
                  '<div class="cart-summary-overlay__variant">',
                  '<span class="cart-summary-overlay__variant-value">', addedDataJSON.selling_plan_allocation.selling_plan.name, '</span>',
                  '</div>'].join('');
                }
                if ($form.data('show-preorder-label')) {
                  variantHtml += "<div class=\"cart-summary-overlay__price-label price-label price-label--preorder\">".concat(theme.strings.products_product_preorder, "</div>");
                } else if ($form.data('show-sale-price-label') && originalVariantPrice > addedDataJSON.original_price) {
                  variantHtml += "<div class=\"cart-summary-overlay__price-label price-label price-label--sale\">".concat(theme.strings.products_labels_sale, "</div>");
                }
  
                var unitPriceHtml = '';
                if (originalVariantPrice > addedDataJSON.final_price) {
                  unitPriceHtml += '<div class="struck-out-price"><span class="theme-money">' + formatMoney(originalVariantPrice, theme.shop.money_format) + '</span></div>';
                }
                unitPriceHtml += '<div><span class="theme-money">' + formatMoney(addedDataJSON.final_price, theme.shop.money_format) + '</span></div>';
                unitPriceHtml += renderUnitPrice(addedDataJSON.unit_price, addedDataJSON.unit_price_measurement, theme.shop.money_format);
  
                var linePriceHtml = '';
                if (originalVariantPrice != addedDataJSON.final_price) {
                  linePriceHtml += '<div class="theme-money struck-out-price">' + formatMoney(originalVariantPrice * addedQty, theme.shop.money_format) + '</div>';
                  linePriceHtml += '<div class="theme-money">' + formatMoney(addedDataJSON.final_line_price, theme.shop.money_format) + '</div>';
                } else {
                  linePriceHtml += '<span class="theme-money">' + formatMoney(addedDataJSON.final_line_price, theme.shop.money_format) + '</span>';
                }
                var lineDiscountHtml = '';
                if (addedDataJSON.line_level_discount_allocations && addedDataJSON.line_level_discount_allocations.length > 0) {
                  lineDiscountHtml += '<ul class="cart-discount-list">';
                  for (var i = 0; i < addedDataJSON.line_level_discount_allocations.length; i++) {
                    var discount_allocation = addedDataJSON.line_level_discount_allocations[i];
                    lineDiscountHtml += [
                    '<li class="cart-discount cart-discount--inline">',
                    '<span class="cart-discount__label">',
                    '<span class="cart-discount__icon">', icons.label, '</span>',
                    '<span class="cart-discount__title">', discount_allocation.discount_application.title, '</span>',
                    '</span>',
                    '<span class="cart-discount__amount theme-money">',
                    formatMoney(discount_allocation.amount, theme.shop.money_format),
                    '</span>',
                    '</li>'].join('');
                  }
                  lineDiscountHtml += '</ul>';
                }
  
                var subtotalHtml = '';
                if (cartDataJSON.cart_level_discount_applications && cartDataJSON.cart_level_discount_applications.length > 0) {
                  subtotalHtml += '<ul class="cart-discount-list">';
                  for (var i = 0; i < cartDataJSON.cart_level_discount_applications.length; i++) {
                    var discount_application = cartDataJSON.cart_level_discount_applications[i];
                    subtotalHtml += [
                    '<li class="cart-discount cart-discount--inline">',
                    '<span class="cart-discount__label">',
                    '<span class="cart-discount__icon">', icons.label, '</span>',
                    '<span class="cart-discount__title">', discount_application.title, '</span>',
                    '</span>',
                    '<span class="cart-discount__amount theme-money">',
                    formatMoney(discount_application.total_allocated_amount, theme.shop.money_format),
                    '</span>',
                    '</li>'].join('');
                  }
                  subtotalHtml += '</ul>';
                }
  
                subtotalHtml += '<span class="cart-summary-overlay__subtotal-mobile-title">' + theme.strings.products_added_notification_subtotal + ':</span> ';
                subtotalHtml += '<span class="cart-summary-overlay__subtotal-amount theme-money">' + formatMoney(cartDataJSON.total_price, theme.shop.money_format_with_cart_code_preference) + '</span>';
  
                //Now we have all the data, build the shade
                var cartShadeHTML = cartPopupTemplate;
                cartShadeHTML = cartShadeHTML.replace('[[title]]', addedDataJSON.product_title);
                cartShadeHTML = cartShadeHTML.replace('[[encoded-title]]', addedDataJSON.product_title.replace(/"/g, '&quot;').replace(/&/g, '&amp;'));
                cartShadeHTML = cartShadeHTML.replace('[[variants]]', variantHtml);
                cartShadeHTML = cartShadeHTML.replace('[[bold_options]]', boldProperties);
                cartShadeHTML = cartShadeHTML.replace(/\[\[quantity\]\]/g, addedQty);
                cartShadeHTML = cartShadeHTML.replace('[[image_url]]', formatImage(addedImage, '170x'));
                cartShadeHTML = cartShadeHTML.split('[[unit_price]]').join(unitPriceHtml);
                cartShadeHTML = cartShadeHTML.split('[[line_price]]').join(linePriceHtml);
                cartShadeHTML = cartShadeHTML.split('[[line_discount]]').join(lineDiscountHtml);
                cartShadeHTML = cartShadeHTML.replace('[[subtotal]]', subtotalHtml);
  
                var $cartShade = $(cartShadeHTML);
                $cartShade.find('#shop-more').bind('click', function () {
                  $cartShade.animate({ top: -$cartShade.outerHeight() }, 500, function () {
                    $(this).remove();
                  });
                  return false;
                });
                $cartShade.prependTo('body').css('top', -$cartShade.outerHeight()).animate({ top: 0 }, 500);
              }, 'html');
              document.addEventListener('dispatch:cart-drawer:refresh', document.querySelector('cart-form').refresh());
              
              document.addEventListener('on:cart:change', refreshHeader());

              if (window.BOLD && BOLD.common && BOLD.common.eventEmitter 
                  && typeof BOLD.common.eventEmitter.emit === 'function'){
                BOLD.common.eventEmitter.emit('BOLD_COMMON_cart_loaded');
              }
            
            }, 'text').fail(function (data) {
              //Enable add button
              $form.find('button[type="submit"]').removeAttr('disabled').each(function () {
                $(this).val($(this).data('previous-value'));
              });
  
              //Not added, show message
              if (typeof data != 'undefined' && typeof data.status != 'undefined') {
                var jsonRes = $.parseJSON(data.responseText);
                showQuickPopup(jsonRes.description, $form.find('button[type="submit"]:first'));
              } else {
                //Some unknown error? Disable ajax and add the old-fashioned way.
                $formContainer.attr('ajax-add-to-cart', 'false');
                $form.submit();
              }
            });
            const submit_button = $(evt.originalEvent.submitter);
            if(submit_button.data('link')){
              setTimeout(function() {
                window.location.href = submit_button.data('link');
              }, 4000);
            }
          }
        });
    });

    function getProductData($form){
        var productData = {};
        var productId = $form.data('product-id');
        var data = null;
        if (!productData[productId]) {
            productData[productId] = JSON.parse(document.getElementById('ProductJson-' + productId).innerHTML);
        }
        data = productData[productId];
        if (!data) {
            console.log('Product data missing (id: ' + $form.data('product-id') + ')');
        }
        return data;    
    }
    function formatMoney(t, r) {
        function e(t, r) {
            return void 0 === t ? r : t;
        }
        function a(t, r, a, o) {
            if (r = e(r, 2),
            a = e(a, ","),
            o = e(o, "."),
            isNaN(t) || null == t)
            return 0;
            t = (t / 100).toFixed(r);
            var n = t.split(".");
            return n[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + a) + (n[1] ? o + n[1] : "");
        }
        "string" == typeof t && (t = t.replace(".", ""));
        var o = "",
        n = /\{\{\s*(\w+)\s*\}\}/,
        i = r || this.money_format;
        switch (i.match(n)[1]) {
            case "amount":
            o = a(t, 2);
            break;
            case "amount_no_decimals":
            o = a(t, 0);
            break;
            case "amount_with_comma_separator":
            o = a(t, 2, ".", ",");
            break;
            case "amount_with_space_separator":
            o = a(t, 2, " ", ",");
            break;
            case "amount_with_period_and_space_separator":
            o = a(t, 2, " ", ".");
            break;
            case "amount_no_decimals_with_comma_separator":
            o = a(t, 0, ".", ",");
            break;
            case "amount_no_decimals_with_space_separator":
            o = a(t, 0, " ", "");
            break;
            case "amount_with_apostrophe_separator":
            o = a(t, 2, "'", ".");
            break;
            case "amount_with_decimal_separator":
            o = a(t, 2, ".", ".");}

        return i.replace(n, o);
    }
    function formatImage(originalImageUrl, format) {
        return originalImageUrl ? originalImageUrl.replace(/^(.*)\.([^\.]*)$/g, '$1_' + format + '.$2') : '';
    }
    function renderUnitPrice(unit_price, unit_price_measurement, money_format){
        if (unit_price && unit_price_measurement) {
            var unitPriceHtml = '<div class="unit-price">';
            unitPriceHtml += "<span class=\"unit-price__price theme-money\">".concat(theme.Shopify.formatMoney(unit_price, money_format), "</span>");
            unitPriceHtml += "<span class=\"unit-price__separator\">".concat(theme.strings.products_product_unit_price_separator, "</span>");
            var unit = unit_price_measurement.reference_unit;
            if (unit_price_measurement.reference_value != 1) {
                unit = unit_price_measurement.reference_value + unit;
            }
            unitPriceHtml += "<span class=\"unit-price__unit\">".concat(unit, "</span>");
            unitPriceHtml += '</div>';
            return unitPriceHtml;
        } else {
            return '';
        }
    }
    function showQuickPopup(message, $origin){
        var $popup = $('<div class="simple-popup"/>');
        var offs = $origin.offset();
        $popup.html(message).css({ 'left': offs.left, 'top': offs.top }).hide();
        $('body').append($popup);
        $popup.css({ marginTop: -$popup.outerHeight() - 10, marginLeft: -($popup.outerWidth() - $origin.outerWidth()) / 2 });
        $popup.fadeIn(200).delay(3500).fadeOut(400, function () {
            $(this).remove();
        });
    }

    function refreshHeader(){
        fetch(`${window.location.origin}?sections=header`)
          .then((response) => response.json())
          .then((data) => {
            const template = document.createElement('template');
            template.innerHTML = data.header;
            const selectorsForRefresh = ['#pageheader .logo-area__right__inner .cart-link .cart-link__icon .cart-link__count'];
            for (let i = 0; i < selectorsForRefresh.length; i += 1) {
              const newEl = template.content.querySelector(selectorsForRefresh[i]);
              const currentEl = document.querySelector(selectorsForRefresh[i]);
              currentEl.innerHTML = newEl.innerHTML;
            }
          });
      };
    if (theme.settings.cartType === 'drawer') {
        $(window).off('.ajaxAddScroll').on('scroll.ajaxAddScroll', function () {
            // Hide notifications on scroll
            $('#cart-summary-overlay #shop-more').triggerHandler('click');
        });
    }

    // function collectionFilter(){
    //   $('.template-collection .link-dropdown__button').click(function(evt){
    //     evt.preventDefault();
    //     var $btn = $(evt.currentTarget),
    //     doExpand = $btn.attr('aria-expanded') == 'false';
    //     $btn.attr('aria-expanded', doExpand);
    //     $btn.css('width', $btn.outerWidth() + 'px');
    //     var newWidth = null,
    //     $optsBox = $btn.next(),
    //     isLeftAligned = $btn.closest('.link-dropdown').hasClass('link-dropdown--left-aligned');
    //     if (!isLeftAligned) {
    //       if (doExpand) {
    //         newWidth = $optsBox.outerWidth();
    //         newWidth += parseInt($optsBox.css('right'));
    //         newWidth -= parseInt($optsBox.find('.link-dropdown__link:first').css('padding-left'));
    //       } else {
    //         newWidth = parseInt($btn.css('padding-right')) + $btn.find('.link-dropdown__button-text').width() + 1;
    //       }
    //       setTimeout(function () {
    //         $btn.css('width', newWidth + 'px');
    //       }, 10);
    //     }
    //   })
    // }
  })

// Build a ring: removing one piece removes the whole ring (setting, stone, their option lines and the finishing fee).
// Runs in the capture phase so it handles the click before the theme's and Bold's cart scripts.
window.addEventListener('click', function (evt) {
  var link = evt.target.closest && evt.target.closest('a[data-bar-build-id]');
  if (!link) return;
  evt.preventDefault();
  evt.stopImmediatePropagation();
  if (link.dataset.barRemoving) return;
  link.dataset.barRemoving = 'true';
  link.style.opacity = '0.5';

  var buildId = link.dataset.barBuildId;
  var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
    .then(function (response) { return response.json(); })
    .then(function (cart) {
      var updates = {};
      cart.items.forEach(function (item) {
        if (item.properties && item.properties._build_a_ring_id === buildId) updates[item.key] = 0;
      });
      var body = { updates: updates };
      // Keep the free setting discount attribute in sync (set by assets/build-a-ring.js)
      var freeSetting = cart.attributes && cart.attributes._free_setting_rings;
      if (freeSetting) {
        var remaining = freeSetting.split(',').filter(function (id) { return id && id !== buildId; });
        body.attributes = {
          _free_setting_rings: remaining.join(','),
          _free_setting: remaining.length ? 'yes' : 'no'
        };
      }
      return fetch(root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      });
    })
    .then(function (response) {
      if (!response.ok) throw new Error(response.status);
      if (link.closest('cart-drawer')) {
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
      } else {
        window.location.reload();
      }
    })
    .catch(function () {
      // Fall back to removing just this line
      window.location.href = link.href;
    });
}, true);